import { useTranslations } from "next-intl";

interface TeamStatistics {
  wins: number;
  losses: number;
  winRate: number;
  setsWon: number;
  setsLost: number;
  gamesWon: number;
  gamesLost: number;
  tiebreaksWon: number;
  tiebreaksLost: number;
}

interface TeamStatisticsComparisonProps {
  teamName: string;
  teamStatistics: TeamStatistics | null;
}

export function TeamStatisticsComparison({
  teamName,
  teamStatistics,
}: TeamStatisticsComparisonProps) {
  const t = useTranslations();

  // If no statistics, don't render anything
  if (!teamStatistics) return null;

  return (
    <div className="space-y-3">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <h3 className="text-xs font-medium text-amber-500/80 uppercase tracking-wider">
          {t("teams.statistics.title")}
        </h3>
        <div className="flex-1 h-px bg-amber-500/20" />
      </div>

      {/* Stats container */}
      <div className="rounded-xl bg-[#0a0c10]/60 border border-amber-500/10 p-3">
        {/* Win rate highlight */}
        <div className="mb-3 pb-3 border-b border-white/5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-white/50 uppercase tracking-wider">
              {t("teams.statistics.winRate")}
            </span>
            <span className="text-base font-semibold text-amber-400">
              {teamStatistics.winRate.toFixed(0)}%
            </span>
          </div>
          <div className="relative h-1 bg-white/5 rounded-full overflow-hidden">
            <div
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full transition-[width] duration-500"
              style={{ width: `${teamStatistics.winRate}%` }}
            />
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          <StatRow
            label={t("teams.statistics.wins")}
            value={teamStatistics.wins}
            variant="positive"
          />
          <StatRow
            label={t("teams.statistics.losses")}
            value={teamStatistics.losses}
            variant="negative"
          />
          <StatRow
            label={t("teams.statistics.setsWon")}
            value={teamStatistics.setsWon}
            variant="positive"
          />
          <StatRow
            label={t("teams.statistics.setsLost")}
            value={teamStatistics.setsLost}
            variant="negative"
          />
          <StatRow
            label={t("teams.statistics.gamesWon")}
            value={teamStatistics.gamesWon}
            variant="positive"
          />
          <StatRow
            label={t("teams.statistics.gamesLost")}
            value={teamStatistics.gamesLost}
            variant="negative"
          />
          <StatRow
            label={t("teams.statistics.tiebreaksWon")}
            value={teamStatistics.tiebreaksWon}
            variant="positive"
          />
          <StatRow
            label={t("teams.statistics.tiebreaksLost")}
            value={teamStatistics.tiebreaksLost}
            variant="negative"
          />
        </div>
      </div>
    </div>
  );
}

// Helper component for displaying a single stat row
function StatRow({
  label,
  value,
  variant = "neutral",
}: {
  label: string;
  value: number | string;
  variant?: "positive" | "negative" | "neutral";
}) {
  const valueColor =
    variant === "positive"
      ? "text-emerald-400"
      : variant === "negative"
        ? "text-red-400"
        : "text-white/90";

  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-xs text-white/50">{label}</span>
      <span className={`text-sm font-medium ${valueColor}`}>{value}</span>
    </div>
  );
}
